import React, { useCallback, useMemo } from 'react';
import { BaseDateRangePicker } from '@/components/uiKit/FormControl';
import { isAfter, isBefore, addMonths, subMonths, addDays, subDays } from 'date-fns';
import { DateRangePreset } from '@/utils/shared/datePicker/dateRangePresets';

type Unit = 'days' | 'months';

interface DatePickerProps {
  from?: Date;
  to?: Date;
  limitUnit?: Unit;
  limitValue?: number;
  dateRangePresets?: DateRangePreset[];
  onDateUpdate?: (range: { from: Date; to: Date }) => void;
}

export const DatePicker: React.FC<DatePickerProps> = ({
  from,
  to,
  limitUnit = 'months',
  limitValue = 2,
  dateRangePresets,
  onDateUpdate,
}) => {
  const getDateWithOffset = useCallback((date: Date, unit: Unit = 'months', offset = 2): Date => {
    return unit === 'months'
      ? offset > 0
        ? addMonths(date, offset)
        : subMonths(date, Math.abs(offset))
      : offset > 0
      ? addDays(date, offset)
      : subDays(date, Math.abs(offset));
  }, []);

  const customDatesRangeLimits = useCallback(
    ({ startDate, endDate, day }: { startDate?: Date; endDate?: Date; day: Date }) => {
      if (startDate && isAfter(day, getDateWithOffset(startDate, limitUnit, limitValue))) return true;
      if (endDate && isBefore(day, getDateWithOffset(endDate, limitUnit, -limitValue))) return true;
      return false;
    },
    [getDateWithOffset, limitUnit, limitValue]
  );

  const defaultFrom = useMemo(
    () => (limitUnit === 'months' ? subMonths(new Date(), limitValue) : subDays(new Date(), limitValue)),
    [limitUnit, limitValue]
  );

  const defaultTo = useMemo(() => new Date(), []);

  const isControlled = from !== undefined || to !== undefined;

  const handleDateUpdate = (date: Date | { from?: Date; to?: Date } | undefined) => {
    if (!date) return;

    let newFrom: Date | undefined;
    let newTo: Date | undefined;

    if (date instanceof Date) {
      newFrom = date;
      newTo = date;
    } else {
      newFrom = date.from;
      newTo = date.to ?? date.from;
    }

    if (newFrom && newTo && onDateUpdate) {
      onDateUpdate({ from: newFrom, to: newTo });
    }
  };

  return (
    <BaseDateRangePicker
      key={`${limitUnit}-${limitValue}`}
      {...(isControlled ? { from, to } : { defaultFrom, defaultTo })}
      mode="range"
      customDatesRangeLimits={customDatesRangeLimits}
      onDateUpdate={handleDateUpdate}
      classNames={{ wrapper: 'w-fit flex justify-end' }}
      variant="button"
      dateRangePresets={dateRangePresets}
    />
  );
};

function getLimitLabelMessage(limitUnit: Unit, limitValue: number): string {
  return `Selection is limited to dates within the past ${limitValue} ${limitUnit} from the selected date`;
}
